import { useCallback, useEffect, useMemo, useState } from "react";
import { tasksAPI } from "../../api/client";
import { apiError } from "./taskUtils";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermission } from "../../hooks/usePermission";

export const NO_SECTION_ID = "__none";

// A project's manual sections, layered over tasks already loaded by
// useSectionedTasks (`board`). Exposes the same interface the sectioned list
// and the board consume (groups, targetFor, canAddTo, createIn, moveTask) so
// they render project sections exactly like date sections.
export default function useProjectSections(projectId, board) {
  const [sections, setSections] = useState([]);
  const [error, setError] = useState("");
  const [movedTo, setMovedTo] = useState({}); // optimistic taskId -> sectionId

  const loadSections = useCallback(async () => {
    try {
      const resp = await tasksAPI.projectSections(projectId);
      setSections(resp.data || []);
    } catch (err) {
      setError(apiError(err, "Could not load sections"));
    }
  }, [projectId]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  // Fresh task data supersedes any optimistic moves.
  useEffect(() => setMovedTo({}), [board.tasks]);

  const sectionOf = useCallback(
    (task) =>
      (task.id in movedTo
        ? movedTo[task.id]
        : task.projectSections?.[projectId]) || NO_SECTION_ID,
    [movedTo, projectId],
  );

  const groups = useMemo(() => {
    const columns = sections.map((s) => ({
      section: { id: s.id, name: s.name, type: "project" },
      tasks: [],
    }));
    const none = {
      section: { id: NO_SECTION_ID, name: "No section", type: "project" },
      tasks: [],
    };
    const byId = new Map(columns.map((g) => [g.section.id, g]));
    for (const task of board.tasks)
      (byId.get(sectionOf(task)) || none).tasks.push(task);
    // "No section" leads when it has tasks, or when there are no sections yet.
    return none.tasks.length || columns.length === 0
      ? [none, ...columns]
      : columns;
  }, [sections, board.tasks, sectionOf]);

  const targetFor = useCallback(
    (section) => ({
      sectionId: section.id === NO_SECTION_ID ? null : section.id,
      hint: `Drop to move to ${section.name}`,
    }),
    [],
  );
  const canCreate = usePermission(PERMISSIONS.TASK_CREATE);
  const canAddTo = useCallback(() => canCreate, [canCreate]);

  const createIn = async (section, name) => {
    try {
      await tasksAPI.create({
        name,
        projectIds: [projectId],
        assigneeId: null,
        projectSectionId: section.id === NO_SECTION_ID ? undefined : section.id,
      });
      await board.loadTasks();
      return true;
    } catch (err) {
      setError(apiError(err, "Could not create task"));
      return false;
    }
  };

  const moveTask = async (task, section) => {
    const sectionId = section.id === NO_SECTION_ID ? null : section.id;
    setMovedTo((prev) => ({ ...prev, [task.id]: sectionId }));
    try {
      await tasksAPI.moveToSection(task.id, projectId, sectionId);
    } catch (err) {
      setError(apiError(err, "Could not move task"));
    }
    board.loadTasks();
  };

  // Section management; each call returns the fresh ordered list.
  const manage = async (request) => {
    setError("");
    try {
      const resp = await request();
      setSections(resp.data || []);
      board.loadTasks();
      return true;
    } catch (err) {
      setError(apiError(err));
      return false;
    }
  };

  return {
    sections,
    error,
    groups,
    targetFor,
    canAddTo,
    createIn,
    moveTask,
    addSection: (name) =>
      manage(() => tasksAPI.createProjectSection(projectId, name)),
    renameSection: (id, name) =>
      manage(() => tasksAPI.renameProjectSection(id, name)),
    deleteSection: (id) => manage(() => tasksAPI.deleteProjectSection(id)),
    reorderSections: (ids) =>
      manage(() => tasksAPI.reorderProjectSections(projectId, ids)),
  };
}
